import { useEffect, useState } from 'react';
import client from '../api/client';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { printJobLabels } from '../qrLabels';
import './Jobs.css';

export default function Jobs() {
  const { org } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    job_number: '',
    description: '',
    location_id: '',
    status: 'queued',
    quantity: '',
    due_date: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [jobsRes, locRes] = await Promise.all([
        client.get('/jobs'),
        client.get('/locations'),
      ]);
      setJobs(jobsRes.data);
      setLocations(locRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await client.post('/jobs', {
        ...form,
        quantity: form.quantity ? parseInt(form.quantity) : null,
      });
      setShowModal(false);
      setForm({ job_number: '', description: '', location_id: '', status: 'queued', quantity: '', due_date: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create job');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await client.put(`/jobs/${id}`, { status });
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await client.delete(`/jobs/${id}`);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'queued': return 'Queued';
      case 'in_progress': return 'In Progress';
      case 'complete': return 'Complete';
      case 'on_hold': return 'On Hold';
      default: return status;
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'queued': return '#6b7280';
      case 'in_progress': return '#3b82f6';
      case 'complete': return '#22c55e';
      case 'on_hold': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  if (loading) return <Layout><div style={{color:'#6b7280', padding:'20px'}}>Loading...</div></Layout>;

  return (
    <Layout>
      <div className="jobs-header">
        <h1>Jobs</h1>
        <div style={{display:'flex', gap:'10px'}}>
          <button className="jobs-btn-cancel" onClick={() => jobs.length && printJobLabels(jobs, org?.slug)} title="Print QR labels for all jobs">Print QR Labels</button>
          <button className="jobs-btn-new" onClick={() => setShowModal(true)}>+ New Job</button>
        </div>
      </div>

      <div className="jobs-table-wrap">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>Job #</th>
              <th>Description</th>
              <th>Location</th>
              <th>Status</th>
              <th>Qty</th>
              <th>Due Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr>
                <td colSpan="7" className="jobs-empty">No jobs yet. Create your first job.</td>
              </tr>
            )}
            {jobs.map(job => (
              <tr key={job.id}>
                <td className="jobs-number">{job.job_number}</td>
                <td className="jobs-desc">{job.description || '--'}</td>
                <td className="jobs-loc">{job.location_name || 'Unassigned'}</td>
                <td>
                  <div
  className="jobs-status-badge"
  style={{ color: statusColor(job.status) }}
  title="Click to change status"
  onClick={() => {
    const statuses = ['queued', 'in_progress', 'on_hold', 'complete'];
    const next = statuses[(statuses.indexOf(job.status) + 1) % statuses.length];
    updateStatus(job.id, next);
  }}
>
  {statusLabel(job.status)}
</div>
                </td>
                <td className="jobs-qty">{job.quantity || '--'}</td>
                <td className="jobs-date">{job.due_date ? new Date(job.due_date).toLocaleDateString() : '--'}</td>
                <td>
                  <button className="jobs-delete" style={{marginRight:'8px', color:'#8b96a5'}} title="Print QR label" onClick={() => printJobLabels([job], org?.slug)}>QR</button>
                  <button className="jobs-delete" onClick={() => deleteJob(job.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="jobs-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="jobs-modal" onClick={e => e.stopPropagation()}>
            <h2>New Job</h2>
            {error && <div className="jobs-modal-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <input
                placeholder="Job Number *"
                value={form.job_number}
                onChange={e => setForm({...form, job_number: e.target.value})}
                required
              />
              <input
                placeholder="Description"
                value={form.description}
                onChange={e => setForm({...form, description: e.target.value})}
              />
              <select
                value={form.location_id}
                onChange={e => setForm({...form, location_id: e.target.value})}
              >
                <option value="">No Location</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
              <select
                value={form.status}
                onChange={e => setForm({...form, status: e.target.value})}
              >
                <option value="queued">Queued</option>
                <option value="in_progress">In Progress</option>
                <option value="on_hold">On Hold</option>
                <option value="complete">Complete</option>
              </select>
              <input
                type="number"
                placeholder="Quantity"
                value={form.quantity}
                onChange={e => setForm({...form, quantity: e.target.value})}
              />
              <input
                type="date"
                value={form.due_date}
                onChange={e => setForm({...form, due_date: e.target.value})}
              />
              <div className="jobs-modal-actions">
                <button type="button" className="jobs-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="jobs-btn-new">Create Job</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}